/**
 * initialDataBuilder.js
 * בונה formData ראשוני מתוך widget.props ו-widget.widgetSettings לצורך עריכה.
 * מחליף את ה-switch הגדול ב-handleEditWidget של Dashboard.jsx.
 */

import { getWidgetTabsConfig } from './widgetTabsConfig';

/**
 * buildInitialData — בונה formData עבור WidgetConfigurator ממצב קיים של רכיב
 * @param {string} componentKey - סוג הרכיב
 * @param {object} widget - הרכיב המלא (props, widgetSettings, templateId)
 * @returns {object} formData
 */
export const buildInitialData = (componentKey, widget) => {
  const config = getWidgetTabsConfig(componentKey);
  const defaults = config?.defaultData || {};
  const props = widget.props || {};
  const settings = widget.widgetSettings || {};

  // בסיס: widgetSettings
  const base = {
    bgColor: settings.bgColor || defaults.bgColor || '',
    borderType: settings.borderType || (settings.hasBorder ? 'colored' : undefined) || defaults.borderType || 'default',
  };

  switch (componentKey) {
    case 'ZmanimWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        customTimes: JSON.parse(JSON.stringify(props.customTimes || [])),
        visibleZmanim: props.visibleZmanim || null,
        tefilotColor: props.tefilotColor || defaults.tefilotColor,
        moadColor: props.moadColor || defaults.moadColor,
        shabbatColor: props.shabbatColor || defaults.shabbatColor,
        showInyaneiHayom: props.showInyaneiHayom || false,
        inyaneiDisplayMode: props.inyaneiDisplayMode || 'section',
        inyaneiEdah: props.inyaneiEdah || 'ashkenaz',
        inyaneiCategories: props.inyaneiCategories || null,
      };

    case 'ShabbatWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        shabbatPrayers: JSON.parse(JSON.stringify(props.customTimes || [])),
        drashot: JSON.parse(JSON.stringify(props.drashot || [])),
        entryIcon: props.entryIcon || defaults.entryIcon,
        exitIcon: props.exitIcon || defaults.exitIcon,
        entryZman: props.entryZman || null,
        exitZman: props.exitZman || null,
        showTimeRule: props.showTimeRule || false,
        showEntryExit: props.showEntryExit ?? true,
        showLeyning: props.showLeyning || false,
        showHaftarah: props.showHaftarah || false,
        leyningDisplayMode: props.leyningDisplayMode || 'summary',
      };

    case 'MoadWidget': {
      const MONTH_STR_TO_NUM = { Nisan:1,Iyar:2,Sivan:3,Tamuz:4,Av:5,Elul:6,Tishrei:7,Cheshvan:8,Kislev:9,Tevet:10,Shevat:11,Adar:12,Adar1:12,Adar2:13 };
      const days = (props.days || []).map(day => ({
        ...day,
        name:  day.name || day.label || '',
        label: day.name || day.label || '',
        hebrewMonth: typeof day.hebrewMonth === 'number' ? day.hebrewMonth : (MONTH_STR_TO_NUM[day.hebrewMonth] || 1),
      }));
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        days: JSON.parse(JSON.stringify(days)),
        moadPrayers: JSON.parse(JSON.stringify(props.customTimes || [])),
        moadDrashot: JSON.parse(JSON.stringify(props.drashot || [])),
        entryIcon: props.entryIcon || defaults.entryIcon,
        exitIcon: props.exitIcon || defaults.exitIcon,
        entryZman: props.globalEntryZman || null,
        exitZman: props.globalExitZman || null,
        showLeyning: props.showLeyning || false,
        showHaftarah: props.showHaftarah || false,
        leyningDisplayMode: props.leyningDisplayMode || 'summary',
      };
    }

    case 'TefilotWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        customTimes: JSON.parse(JSON.stringify(props.customTimes || [])),
        recurringDisplay: props.recurringDisplay || 'compact',
        showRuleMode: props.showRuleMode || false,
        highlightNextMinyan: props.highlightNextMinyan ?? false,
        timelineValidFrom: props.timelineValidFrom || '',
        timelineValidTo: props.timelineValidTo || '',
      };

    case 'InyaneiHaYomWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        edah: props.edah || 'ashkenaz',
        showTachanun: props.showTachanun ?? true,
        showHallel: props.showHallel ?? true,
        showPrayerAdditions: props.showPrayerAdditions ?? true,
        showSeasonalPrayers: props.showSeasonalPrayers ?? true,
        showSeasonalTransitions: props.showSeasonalTransitions ?? true,
        showSelichot: props.showSelichot ?? true,
        showWeekdayPrayers: props.showWeekdayPrayers ?? true,
        showShabbatAdditions: props.showShabbatAdditions ?? true,
        showMotzaeiShabbat: props.showMotzaeiShabbat ?? true,
        showSeasonalCustoms: props.showSeasonalCustoms ?? true,
        showLiturgy: props.showLiturgy ?? true,
        showLeyning: props.showLeyning || false,
        showHaftarah: props.showHaftarah || false,
        showMourningPeriods: props.showMourningPeriods ?? true,
        showLimudim: props.showLimudim ?? false,
        lookaheadDays: props.lookaheadDays ?? 0,
      };

    case 'LimudimWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        showDafYomi: props.showDafYomi ?? true,
        showMishnaYomi: props.showMishnaYomi ?? true,
        showNachYomi: props.showNachYomi ?? false,
        showRambam1: props.showRambam1 ?? true,
        showRambam3: props.showRambam3 ?? false,
        showYerushalmiVilna: props.showYerushalmiVilna ?? false,
        showYerushalmiSchottenstein: props.showYerushalmiSchottenstein ?? false,
        showChofetzChaim: props.showChofetzChaim ?? false,
        showShemiratHaLashon: props.showShemiratHaLashon ?? false,
        showSeferHaMitzvot: props.showSeferHaMitzvot ?? false,
        showKitzurShulchanAruch: props.showKitzurShulchanAruch ?? false,
        showArukhHaShulchanYomi: props.showArukhHaShulchanYomi ?? false,
        showPsalms: props.showPsalms ?? false,
        showTanakhYomi: props.showTanakhYomi ?? false,
        showPirkeiAvotSummer: props.showPirkeiAvotSummer ?? false,
        showPerekYomi: props.showPerekYomi ?? false,
        showDafWeekly: props.showDafWeekly ?? false,
        showDirshuAmudYomi: props.showDirshuAmudYomi ?? false,
      };

    case 'OmerWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        nusach: props.nusach || 'ashkenaz',
        showBracha: props.showBracha ?? true,
        showFullNusach: props.showFullNusach ?? true,
        showLeshemYichud: props.showLeshemYichud ?? false,
        showPasuk: props.showPasuk || 'none',
        showAfterCount: props.showAfterCount ?? false,
        showPreview: props.showPreview ?? false,
      };

    case 'HalachicTimesWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        visibleZmanim: props.visibleZmanim || null,
        showCountdown: props.showCountdown ?? true,
      };

    case 'TextWidget':
      return { ...defaults, ...base, text: props.text || '' };

    case 'ImageWidget':
      return { ...defaults, ...base, imageUrl: props.imageUrl || '' };

    case 'DocumentWidget':
      return {
        ...defaults, ...base,
        documentUrl: props.documentUrl || '',
        documentType: props.documentType || '',
        fileName: props.fileName || '',
        pageNumber: props.pageNumber || 1,
        autoScroll: props.autoScroll || false,
        scrollInterval: props.scrollInterval || 10,
      };

    case 'WeatherWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
      };

    case 'ClockWidget':
      return {
        ...defaults, ...base,
        textColor: props.textColor || '#1f3a5f',
      };

    case 'NoticeBoardWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        notices: JSON.parse(JSON.stringify(props.data?.notices || [])),
      };

    case 'EmergencyWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        emergencyData: JSON.parse(JSON.stringify(props.data || { volunteers: { medic: [], paramedic: [], maar: [] } })),
      };

    case 'ContactsWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        contacts: JSON.parse(JSON.stringify(props.data?.contacts || [])),
      };

    case 'SimchaWidget': {
      const d = props.data || {};
      return {
        ...defaults, ...base,
        title:     props.title    || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        simchas:   JSON.parse(JSON.stringify(d.simchas || [])),
        showTopGreeting:    d.showTopGreeting    ?? defaults.showTopGreeting,
        topGreeting:        d.topGreeting        || defaults.topGreeting,
        topBgColor:         d.topBgColor         ?? defaults.topBgColor,
        topTextColor:       d.topTextColor       ?? defaults.topTextColor,
        topFontFamily:      d.topFontFamily      || defaults.topFontFamily,
        topFontSize:        d.topFontSize        || defaults.topFontSize,
        topSymbol:          d.topSymbol          !== undefined ? d.topSymbol    : defaults.topSymbol,
        showBottomGreeting: d.showBottomGreeting ?? defaults.showBottomGreeting,
        bottomGreeting:     d.bottomGreeting     || defaults.bottomGreeting,
        bottomBgColor:      d.bottomBgColor      ?? defaults.bottomBgColor,
        bottomTextColor:    d.bottomTextColor    ?? defaults.bottomTextColor,
        bottomFontFamily:   d.bottomFontFamily   || defaults.bottomFontFamily,
        bottomFontSize:     d.bottomFontSize     || defaults.bottomFontSize,
        bottomSymbol:       d.bottomSymbol       !== undefined ? d.bottomSymbol : defaults.bottomSymbol,
      };
    }

    case 'NitzavSpotlightWidget': {
      const data = props.data ? JSON.parse(JSON.stringify(props.data)) : {};
      if (!data.iconId) data.iconId = 'star';
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        nitzavData: data,
      };
    }

    case 'ExpensesAnalyticsWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        categories: JSON.parse(JSON.stringify(props.data?.categories || defaults.categories)),
        freeText: props.freeText || '',
      };

    case 'FinancialGaugeWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        incomeData: JSON.parse(JSON.stringify(props.data?.incomeData || [])),
        expensesData: JSON.parse(JSON.stringify(props.data?.expensesData || [])),
      };

    case 'ParnasWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        parnasType: props.parnasType || defaults.parnasType,
        parnasTypeCustomLabel: props.parnasTypeCustomLabel || '',
        parnasTypeIcon: props.parnasTypeIcon || 'star',
        sponsors: JSON.parse(JSON.stringify(props.sponsors || [])),
        tileColors: { ...defaults.tileColors, ...(props.tileColors || {}) },
        placeholderName: props.placeholderName ?? defaults.placeholderName,
        placeholderNote: props.placeholderNote ?? defaults.placeholderNote,
        weeklyYearDisplayYear: props.weeklyYearDisplayYear ?? null,
        weeksAheadCount: props.weeksAheadCount ?? 8,
        honorCardPriority: props.honorCardPriority || 'auto',
        includedSlotTypes: { ...defaults.includedSlotTypes, ...(props.includedSlotTypes || {}) },
      };

    case 'SeatingMapWidget':
      return {
        ...defaults, ...base,
        title: props.title || defaults.title,
        showTitle: props.showTitle ?? defaults.showTitle,
        mapData: props.mapData || null,
      };

    case 'NextMinyaWidget':
      return {
        ...defaults, ...base,
        title:        props.title        || defaults.title,
        showTitle:    props.showTitle    ?? defaults.showTitle,
        tefilotColor: props.tefilotColor || defaults.tefilotColor,
        moadColor:    props.moadColor    || defaults.moadColor,
        shabbatColor: props.shabbatColor || defaults.shabbatColor,
      };

    default:
      return { ...defaults, ...base };
  }
};
